import { Box, Popper, Stack } from '@ace-clublabs/shared-component-library';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import SearchResultItem from './SearchResultItem';
import type { SearchResult } from './types';

interface SearchResultsFooter {
  label: string;
  onClick: () => void;
}

interface SearchResultsListProps {
  results?: SearchResult[];
  onSelect: (result: SearchResult) => void;
  open: boolean;
  highlightedIndex?: number;
  anchorEl?: HTMLElement | null;
  footer?: SearchResultsFooter;
}

const SearchResultsList: React.FC<SearchResultsListProps> = ({
  results,
  onSelect,
  open,
  highlightedIndex,
  anchorEl,
  footer,
}) => {
  const listRef = useRef<VirtuosoHandle>(null);
  // Keep the Popper mounted during the exit animation
  const [shouldMount, setShouldMount] = useState(open);

  // Mount the Popper when open becomes true
  useEffect(() => {
    if (open) {
      setShouldMount(true);
    }
  }, [open]);

  // Handle the exit animation complete
  const handleAnimationComplete = () => {
    if (!open) {
      setShouldMount(false);
    }
  };

  useEffect(() => {
    if (listRef.current && highlightedIndex !== undefined) {
      listRef.current.scrollIntoView({
        index: highlightedIndex,
        align: 'center',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  const totalHeight = useMemo(() => {
    const footerHeight = footer ? 44 : 0;
    if (!results?.length) return footerHeight;

    const singleRowItemHeight = 49;
    const doubleRowItemHeight = 71;
    const singleRowItemCount = results.filter(
      (result) => !result.subtitle,
    ).length;
    const doubleRowItemCount = results.length - singleRowItemCount;
    return (
      singleRowItemCount * singleRowItemHeight +
      doubleRowItemCount * doubleRowItemHeight +
      footerHeight
    );
  }, [results, footer]);

  const content = (
    <AnimatePresence onExitComplete={handleAnimationComplete}>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: '100%' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Stack
            direction='column'
            sx={[
              {
                backgroundColor: 'white',
                borderRadius: 1,
                boxShadow: 2,
                height: `min(${totalHeight}px, 100%)`,
                overflow: 'hidden',
              },
              !!anchorEl && {
                maxHeight: '250px',
              },
            ]}
            data-testid='search-results-list'
          >
            <Virtuoso
              ref={listRef}
              data={results}
              itemContent={(_, result) => (
                <SearchResultItem
                  {...result}
                  key={result.id}
                  onClick={() => onSelect(result)}
                  highlighted={highlightedIndex === results?.indexOf(result)}
                />
              )}
              style={{ height: '100%' }}
            />
            {footer && (
              <Box
                component='button'
                onClick={footer.onClick}
                sx={{
                  width: '100%',
                  px: 1,
                  py: 1.5,
                  border: 'none',
                  borderTop: '1px solid',
                  borderColor: 'grey.300',
                  backgroundColor: 'grey.100',
                  borderRadius: 0,
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
              >
                {footer.label}
              </Box>
            )}
          </Stack>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!anchorEl) {
    return content;
  }

  // Don't render Popper at all if we shouldn't mount it
  if (!shouldMount && !open) {
    return null;
  }

  return (
    <Popper
      open={shouldMount} // Keep Popper mounted during exit animation
      anchorEl={anchorEl}
      placement='bottom-start'
      sx={[
        (theme) => ({
          height: `min(${totalHeight}px, 250px)`,
          width: anchorEl ? anchorEl.clientWidth : '100%',
          zIndex: theme.zIndex.modal,
          mt: '8px !important',
        }),
      ]}
    >
      {content}
    </Popper>
  );
};

export default SearchResultsList;
